import { useState } from 'react';
import { Copy, CheckCircle, Share2, UserCheck, Users } from 'lucide-react';
import type { PortalUser } from '../AffiliatePortalApp';
import { AlertModal } from '../../components/AlertModal';

/**
 * ReferralCode — affiliate's referral link tab.
 *
 * Two link cards:
 *   1. Refer a client (always shown) — new signup.oasisautokonnect.com/r/<code>
 *      URL that auto-attributes the prospect to this affiliate at intake.
 *   2. Refer another affiliate (only when tier_enabled && second_level_enabled) —
 *      existing affiliate-application URL. Affiliates without both flags don't
 *      see this card at all (per design — not greyed out, simply absent).
 */
export default function ReferralCode({ user }: { user: PortalUser }) {
  const [copied, setCopied] = useState<string | null>(null);
  const [copyError, setCopyError] = useState<string | null>(null);

  const code = user.referral_code;
  const prospectSignupUrl = `https://signup.oasisautokonnect.com/r/${code}`;
  const affiliateApplyUrl = `https://affiliate.oasisautokonnect.com/apply?ref=${code}`;
  const canReferAffiliate = !!(user.tier_enabled && user.second_level_enabled);

  const copy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      setCopyError(`Could not copy automatically. Please copy this manually:\n\n${text}`);
    }
  };

  return (
    <div className="max-w-3xl">
      <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">Referral Links</h2>
      <p className="text-sm text-slate-600 dark:text-slate-300 mb-6">
        Share these links to track who you refer. When a new customer signs up through your link,
        you earn commission on their monthly plan payments.
      </p>

      {/* Affiliate's referral code */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-sm p-6 mb-4">
        <div className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">Your Referral Code</div>
        <div className="flex items-center gap-3">
          <div className="text-4xl font-bold font-mono text-blue-600">{code}</div>
          <button onClick={() => copy(code, 'code')} className="flex items-center gap-1 px-3 py-2 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-sm">
            {copied === 'code' ? <><CheckCircle className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy</>}
          </button>
        </div>
      </div>

      {/* Card 1: Refer a client (always shown) */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-sm p-6 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <UserCheck className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          <h3 className="font-semibold text-slate-900 dark:text-white">Refer a client</h3>
        </div>
        <p className="text-sm text-slate-600 dark:text-slate-300 mb-3">
          Send this to anyone interested in becoming an OAK client. When they sign up, the referral
          is automatically attributed to you.
        </p>
        <LinkRow url={prospectSignupUrl} onCopy={() => copy(prospectSignupUrl, 'prospect')} copied={copied === 'prospect'} />
      </div>

      {/* Card 2: Refer another affiliate (gated) */}
      {canReferAffiliate ? (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-sm p-6 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            <h3 className="font-semibold text-slate-900 dark:text-white">Refer another affiliate</h3>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-300 mb-3">
            Send this to someone you'd like to bring into the affiliate program. Applicants
            through this link are linked to you for override commissions.
          </p>
          <LinkRow url={affiliateApplyUrl} onCopy={() => copy(affiliateApplyUrl, 'apply')} copied={copied === 'apply'} />
        </div>
      ) : null}

      <div className="mt-4 text-xs text-slate-500 dark:text-slate-400 flex items-start gap-2">
        <Share2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
        <span>
          Your referral code <strong>{code}</strong> is automatically attached to{' '}
          {canReferAffiliate ? 'these links' : 'this link'}, so no additional setup is needed.
        </span>
      </div>

      <AlertModal isOpen={!!copyError} title="Copy" variant="info" message={copyError || ''} onClose={() => setCopyError(null)} />
    </div>
  );
}

function LinkRow({ url, onCopy, copied }: { url: string; onCopy: () => void; copied: boolean }) {
  return (
    <div className="flex gap-2">
      <input type="text" value={url} readOnly className="flex-1 border rounded px-3 py-2 text-sm bg-gray-50 dark:bg-slate-800 font-mono" />
      <button onClick={onCopy} className="flex items-center gap-1 px-3 py-2 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-sm whitespace-nowrap">
        {copied ? <><CheckCircle className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy</>}
      </button>
    </div>
  );
}
